export interface Point {
  x: number;
  y: number;
}

export interface ActorFootprint {
  radiusX: number;
  radiusY: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Polygon {
  kind: 'polygon';
  id: string;
  points: readonly Point[];
}

export interface RectShape extends Rect {
  kind: 'rect';
  id: string;
}

export type CollisionShape = RectShape | Polygon;

export function boundsOfShape(shape: Rect | CollisionShape): Rect {
  if (!('points' in shape)) return { x: shape.x, y: shape.y, w: shape.w, h: shape.h };

  if (shape.points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const point of shape.points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function pointInPolygon(point: Point, polygon: Polygon): boolean {
  let inside = false;

  for (
    let index = 0, previous = polygon.points.length - 1;
    index < polygon.points.length;
    previous = index++
  ) {
    const currentPoint = polygon.points[index];
    const previousPoint = polygon.points[previous];
    if (!currentPoint || !previousPoint) continue;

    const crossesRay =
      currentPoint.y > point.y !== previousPoint.y > point.y &&
      point.x <
        ((previousPoint.x - currentPoint.x) * (point.y - currentPoint.y)) /
          (previousPoint.y - currentPoint.y) +
          currentPoint.x;

    if (crossesRay) inside = !inside;
  }

  return inside;
}

export function pointInRect(point: Point, rect: Rect): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.w &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.h
  );
}

export function validatePolygon(polygon: Polygon): boolean {
  return (
    polygon.points.length >= 3 &&
    polygon.points.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
  );
}
